/**
 * Listan bakom en rad i Enhetshälsa.
 * Samma widget som tidigare: regnummer, adress och tilldelning.
 */

import React, { Component, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import GpsDataLagWidget from './GpsDataLagWidget';
import LowBatteryWidget from './LowBatteryWidget';
import UnpluggedWidget from './UnpluggedWidget';

export type DeviceHealthKind = 'lowBattery' | 'unplugged' | 'dataLag';

class DetailErrorBoundary extends Component<{ children: ReactNode; message: string }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (this.state.failed) {
      return <p className="py-6 text-sm text-textSecondary">{this.props.message}</p>;
    }
    return this.props.children;
  }
}

function DetailBody({ kind }: { kind: DeviceHealthKind }) {
  switch (kind) {
    case 'lowBattery':
      return <LowBatteryWidget startExpanded />;
    case 'unplugged':
      return <UnpluggedWidget startExpanded />;
    case 'dataLag':
      return <GpsDataLagWidget startExpanded />;
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
}

const DeviceHealthDetail: React.FC<{ kind: DeviceHealthKind; onClose: () => void }> = ({ kind, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 sm:p-8" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t(`deviceHealth.${kind}`)}
        className="relative mt-8 w-full max-w-3xl rounded-xl border border-borderDefault bg-bgCard p-4 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-lg p-1 text-textTertiary hover:bg-bgHover"
          aria-label={t('common.close')}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="pr-8">
          <DetailErrorBoundary message={t('common.pageErrorDescription')}>
            <DetailBody kind={kind} />
          </DetailErrorBoundary>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DeviceHealthDetail;
